'use client'

import { useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export function DocumentUploadStep({
  docLabel,
  onContinue,
}: {
  docLabel: string
  onContinue: (file: File) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 py-6">
        <div
          className={cn(
            'flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border-2 border-dashed p-5 text-center transition-colors',
            dragOver ? 'border-primary bg-primary/5' : 'border-border',
          )}
          role="button"
          tabIndex={0}
          aria-label={`Upload your ${docLabel}`}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            const dropped = e.dataTransfer.files?.[0]
            if (dropped) setFile(dropped)
          }}
        >
          <Upload className="text-muted-foreground size-5" />
          <span className="text-foreground text-sm font-medium">
            {file?.name ?? `Drop your ${docLabel} here`}
          </span>
          {!file && (
            <span className="text-muted-foreground text-sm">
              or{' '}
              <span className="text-primary underline-offset-4 hover:underline">
                click to browse files
              </span>
            </span>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.csv,.xlsx"
          hidden
          onChange={(e) => {
            const picked = e.target.files?.[0]
            if (picked) setFile(picked)
          }}
        />
        <Button className="w-full" onClick={() => file && onContinue(file)} disabled={!file}>
          Continue
        </Button>
      </CardContent>
    </Card>
  )
}
